import React from "react";
import { Droppable } from "react-beautiful-dnd";
import { Tooltip, Button } from "antd";
import { ReactSVG } from "react-svg";
import { useSelector } from "react-redux";
import { sumBy } from "lodash";
import moment from "moment";

import IconHide from "./IconHide";
import IconLock from "./IconLock";
import IconSwap from "./IconSwap";
import Steps from "./Steps";
import DriverAssign from "./DriverAssign";
import { getListStyle, isDiffTwoArrayStops } from "./Utils";
import { DATE_FORMAT_BACKEND } from "../../constants";
import IconSync from "./IconSync";

const DriverList = ({
  routes,
  solutions,
  originSolutions,
  hours,
  minutes,
  startTime,
  onClickStep,
  onClickRoute,
  onClickSwap,
  onClickSync,
  handleCloseStepPopup,
  moveToUnscheduled,
  stopsHash,
  stepsHovered,
  setStepsHovered,
  onHoverStep,
  onHoverRoute,
  isDragging,
  onClickLock,
  onClickHide,
  driverIdsLock,
  driverIdsHide,
  actionDnd,
  routeType,
  setShowMoreRoute,
  oneHourPx,
  isLive,
  selectedMultipleSteps,
  setAllInclusiveMultipleSteps,
  multipleStepState,
  stepClicked,
  isGmpro,
}) => {
  const { user } = useSelector(state => state.auth);
  const { item: routePlan } = useSelector(state => state.routePlan);
  const orders = routePlan.orders;
  const canEdit = !user?.roles?.includes("user");
  const currentDate = moment().format(DATE_FORMAT_BACKEND);
  const formatDeliveryDate = moment(routePlan.deliveryDate).format(DATE_FORMAT_BACKEND);
  const isSameDate = moment(currentDate, DATE_FORMAT_BACKEND).isSame(moment(formatDeliveryDate, DATE_FORMAT_BACKEND));
  return (
    <>
      {routes.map((route, index) => {
        const driver = route.driver;
        const steps = solutions[driver._id];
        const originSteps = originSolutions[driver._id];
        const isDiff = isDiffTwoArrayStops(steps, originSteps);

        const isLock = driverIdsLock.includes(driver._id);
        const isHide = driverIdsHide.includes(driver._id);
        const capacity = driver.capacity || "∞";
        const load =
          sumBy(
            orders.filter(o => route.orders.includes(o._id)),
            "load",
          ) || 0;
        let loadPercent = Number((load / capacity) * 100).toFixed();
        if (loadPercent > 100) loadPercent = 100;
        const workingPercent = route.summary?.workingPercentage || 0;
        const percent = loadPercent > workingPercent ? loadPercent : workingPercent;
        const percentLevel = percent > 80 ? "high" : capacity > 50 ? "medium" : "low";
        return (
          <div
            key={`route-${index}`}
            className="tr"
            style={{
              minWidth: (hours.length + 1) * oneHourPx,
              width: "100%",
            }}>
            <div className={`td ${isDragging && "isDragging"}`}>
              <div className="driver-name">
                {canEdit && <IconLock isLock={isLock} onClickLock={() => onClickLock(driver._id)} />}
                <IconHide isHide={isHide} onClickHide={() => onClickHide(driver._id)} />
                <div className="driver-info">
                  <Tooltip title={driver.name}>
                    <div className="name" onClick={() => onClickRoute(route, null)}>
                      {driver.name}
                    </div>
                  </Tooltip>
                  <div className={`working-time ${percentLevel}`}>
                    <div className="capacity">
                      <div className="text">
                        {/*{load}/{capacity}*/}
                        {route.shiftStart || "N/A"} - {route.shiftEnd || "N/A"}
                      </div>
                      <div className="completed" style={{ width: loadPercent + "%" }}></div>
                    </div>
                    <div className="percent">{workingPercent + "%"}</div>
                  </div>
                </div>
                <Button
                  className="btn-summary"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    marginLeft: "10px",
                  }}
                  onClick={() => {
                    onClickRoute(route, "start");
                    setShowMoreRoute(true);
                  }}>
                  <ReactSVG
                    className="icon"
                    style={{
                      height: "16px",
                    }}
                    src="/icons/summary.svg"
                  />
                </Button>
                {canEdit && (
                  <>
                    <IconSwap className="swap" onClickSwap={() => onClickSwap(route)} />
                    {routePlan.type === "VRP" && <IconSync className="sync" onClickSync={() => onClickSync(route)} />}
                  </>
                )}
              </div>
              <DriverAssign
                driver={driver}
                stepsHovered={stepsHovered}
                setStepsHovered={setStepsHovered}
                onClickStep={onClickStep}
                onHoverStep={onHoverStep}
                handleCloseStepPopup={handleCloseStepPopup}
                isDragging={isDragging}
                actionDnd={actionDnd}
                assignStops={solutions[`assign_${driver._id}`]}
                isLive={isLive}
                solutionId={`assign_${driver._id}`}
                selectedMultipleSteps={selectedMultipleSteps}
                setAllInclusiveMultipleSteps={setAllInclusiveMultipleSteps}
                multipleStepState={multipleStepState}
              />
            </div>
            <Droppable
              key={driver._id}
              droppableId={driver._id}
              direction="horizontal"
              isDropDisabled={actionDnd === "optimize" || isLive || isGmpro}>
              {(provided, snapshot) => (
                <div
                  className={`td ${isDragging && "isDragging"} ${actionDnd}`}
                  ref={provided.innerRef}
                  style={{
                    ...getListStyle(isDragging, snapshot.isDraggingOver, actionDnd === "optimize"),
                  }}>
                  <Steps
                    routePlan={routePlan}
                    driver={driver}
                    route={route}
                    steps={steps}
                    minutes={minutes}
                    startTime={startTime}
                    onClickStep={onClickStep}
                    onClickRoute={onClickRoute}
                    handleCloseStepPopup={handleCloseStepPopup}
                    moveToUnscheduled={moveToUnscheduled}
                    stopsHash={stopsHash}
                    stepsHovered={stepsHovered}
                    setStepsHovered={setStepsHovered}
                    onHoverStep={onHoverStep}
                    onHoverRoute={onHoverRoute}
                    routeType={routeType}
                    setShowMoreRoute={setShowMoreRoute}
                    oneHourPx={oneHourPx}
                    isSameDate={isSameDate}
                    selectedMultipleSteps={selectedMultipleSteps}
                    setAllInclusiveMultipleSteps={setAllInclusiveMultipleSteps}
                    multipleStepState={multipleStepState}
                    stepClicked={stepClicked}
                    isDiff={isDiff}
                    indexRoute={index}
                  />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        );
      })}
    </>
  );
};

export default DriverList;
